/**
 * High Contrast Mode Support Utility
 * Detects browser capabilities and high contrast preferences
 * Adds appropriate classes to document for CSS fallbacks
 */

class HighContrastSupport {
  constructor() {
    this.init();
  }

  init() {
    // Detect browser capabilities on load
    this.detectBrowserCapabilities();
    
    // Listen for high contrast mode changes
    this.setupHighContrastListeners();
    
    // Apply initial high contrast settings
    this.applyHighContrastMode();
  }

  /**
   * Detect browser capabilities and add fallback classes
   */
  detectBrowserCapabilities() {
    const html = document.documentElement;
    
    // Test CSS Custom Properties support
    if (!this.supportsCSSCustomProperties()) {
      html.classList.add('no-css-custom-properties');
    }
    
    // Test backdrop-filter support
    if (!this.supportsBackdropFilter()) {
      html.classList.add('no-backdrop-filter');
    }
    
    // Test gradient support
    if (!this.supportsGradients()) {
      html.classList.add('no-gradients');
    }
    
    // Test forced-colors support
    if (!this.supportsForcedColors()) {
      html.classList.add('no-forced-colors');
    }
  }

  /**
   * Test if browser supports CSS Custom Properties
   */
  supportsCSSCustomProperties() {
    try {
      return CSS.supports('color', 'var(--test)');
    } catch (e) {
      return false;
    }
  }

  /**
   * Test if browser supports backdrop-filter
   */
  supportsBackdropFilter() {
    try {
      return CSS.supports('backdrop-filter', 'blur(1px)') || 
             CSS.supports('-webkit-backdrop-filter', 'blur(1px)');
    } catch (e) {
      return false;
    }
  }

  /**
   * Test if browser supports gradients
   */
  supportsGradients() {
    try {
      return CSS.supports('background', 'linear-gradient(red, blue)');
    } catch (e) {
      return false;
    }
  }

  /**
   * Test if browser supports forced-colors media query
   */
  supportsForcedColors() {
    try {
      return window.matchMedia('(forced-colors: active)').matches !== undefined;
    } catch (e) {
      return false;
    }
  }

  /**
   * Setup listeners for high contrast mode changes
   */
  setupHighContrastListeners() {
    // Listen for prefers-contrast changes
    if (window.matchMedia) {
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
      const forcedColorsQuery = window.matchMedia('(forced-colors: active)');
      
      // Add listeners for changes
      if (highContrastQuery.addEventListener) {
        highContrastQuery.addEventListener('change', () => {
          this.applyHighContrastMode();
        });
      } else if (highContrastQuery.addListener) {
        // Fallback for older browsers
        highContrastQuery.addListener(() => {
          this.applyHighContrastMode();
        });
      }
      
      if (forcedColorsQuery.addEventListener) {
        forcedColorsQuery.addEventListener('change', () => {
          this.applyHighContrastMode();
        });
      } else if (forcedColorsQuery.addListener) {
        // Fallback for older browsers
        forcedColorsQuery.addListener(() => {
          this.applyHighContrastMode();
        });
      }
    }
  }

  /**
   * Apply high contrast mode settings
   */
  applyHighContrastMode() {
    const html = document.documentElement;
    
    // Remove existing high contrast classes
    html.classList.remove('high-contrast', 'forced-colors', 'windows-high-contrast');
    
    // Check for high contrast preferences
    if (this.isHighContrastMode()) {
      html.classList.add('high-contrast');
      
      // Disable complex animations and effects
      this.disableComplexEffects();
      
      // Enhance focus indicators
      this.enhanceFocusIndicators();
    }
    
    if (this.isForcedColorsMode()) {
      html.classList.add('forced-colors');
    }
    
    if (this.isWindowsHighContrastMode()) {
      html.classList.add('windows-high-contrast');
    }
  }

  /**
   * Check if high contrast mode is active
   */
  isHighContrastMode() {
    if (window.matchMedia) {
      return window.matchMedia('(prefers-contrast: high)').matches;
    }
    return false;
  }

  /**
   * Check if forced colors mode is active
   */
  isForcedColorsMode() {
    if (window.matchMedia) {
      try {
        return window.matchMedia('(forced-colors: active)').matches;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  /**
   * Check if Windows High Contrast mode is active
   */
  isWindowsHighContrastMode() {
    if (window.matchMedia) {
      try {
        return window.matchMedia('(-ms-high-contrast: active)').matches;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  /**
   * Disable complex animations and effects in high contrast mode
   */
  disableComplexEffects() {
    const style = document.createElement('style');
    style.id = 'high-contrast-overrides';
    
    // Remove existing style if it exists
    const existingStyle = document.getElementById('high-contrast-overrides');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    style.textContent = `
      .high-contrast .interactive-primary::before,
      .high-contrast .interactive-secondary::before {
        display: none !important;
      }
      
      .high-contrast .interactive-primary:hover,
      .high-contrast .interactive-secondary:hover {
        transform: none !important;
        filter: none !important;
        box-shadow: none !important;
      }
      
      .high-contrast .shadow-primary,
      .high-contrast .shadow-accent,
      .high-contrast .shadow-glow {
        box-shadow: none !important;
      }
      
      .high-contrast .text-gradient-primary,
      .high-contrast .text-gradient-secondary,
      .high-contrast .text-gradient-accent {
        background: none !important;
        color: #ffffff !important;
        -webkit-background-clip: unset !important;
        -webkit-text-fill-color: unset !important;
        background-clip: unset !important;
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * Enhance focus indicators for high contrast mode
   */
  enhanceFocusIndicators() {
    // Add enhanced focus styles
    const focusStyle = document.createElement('style');
    focusStyle.id = 'high-contrast-focus';
    
    // Remove existing style if it exists
    const existingFocusStyle = document.getElementById('high-contrast-focus');
    if (existingFocusStyle) {
      existingFocusStyle.remove();
    }
    
    focusStyle.textContent = `
      .high-contrast button:focus,
      .high-contrast input:focus,
      .high-contrast textarea:focus,
      .high-contrast select:focus,
      .high-contrast a:focus,
      .high-contrast .focus-ring:focus {
        outline: 3px solid #ffffff !important;
        outline-offset: 2px !important;
        box-shadow: 0 0 0 5px #000000 !important;
      }
      
      .high-contrast .nav-link:focus {
        background-color: #333333 !important;
        color: #ffffff !important;
      }
    `;
    
    document.head.appendChild(focusStyle);
  }

  /**
   * Get current high contrast status
   */
  getStatus() {
    return {
      highContrast: this.isHighContrastMode(),
      forcedColors: this.isForcedColorsMode(),
      windowsHighContrast: this.isWindowsHighContrastMode(),
      cssCustomProperties: this.supportsCSSCustomProperties(),
      backdropFilter: this.supportsBackdropFilter(),
      gradients: this.supportsGradients(),
      forcedColorsSupport: this.supportsForcedColors()
    };
  }

  /**
   * Manually toggle high contrast mode for testing
   */
  toggleHighContrast() {
    const html = document.documentElement;
    
    if (html.classList.contains('high-contrast')) {
      html.classList.remove('high-contrast');
      
      // Remove override styles
      const overrideStyle = document.getElementById('high-contrast-overrides');
      const focusStyle = document.getElementById('high-contrast-focus');
      
      if (overrideStyle) overrideStyle.remove();
      if (focusStyle) focusStyle.remove();
    } else {
      html.classList.add('high-contrast');
      this.disableComplexEffects();
      this.enhanceFocusIndicators();
    }
  }

  /**
   * Test all interactive elements for visibility in high contrast mode
   */
  testHighContrastVisibility() {
    const results = [];
    
    // Test buttons
    const buttons = document.querySelectorAll('button, .btn-primary, .btn-secondary, .btn-accent');
    buttons.forEach((button, index) => {
      const styles = window.getComputedStyle(button);
      const backgroundColor = styles.backgroundColor;
      const color = styles.color;
      const borderColor = styles.borderColor;
      
      results.push({
        element: `button-${index}`,
        type: 'button',
        backgroundColor,
        color,
        borderColor,
        visible: backgroundColor !== color // Simple visibility check
      });
    });
    
    // Test form inputs
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach((input, index) => {
      const styles = window.getComputedStyle(input);
      const backgroundColor = styles.backgroundColor;
      const color = styles.color;
      const borderColor = styles.borderColor;
      
      results.push({
        element: `input-${index}`,
        type: 'input',
        backgroundColor,
        color,
        borderColor,
        visible: backgroundColor !== color
      });
    });
    
    // Test links
    const links = document.querySelectorAll('a');
    links.forEach((link, index) => {
      const styles = window.getComputedStyle(link);
      const color = styles.color;
      const textDecoration = styles.textDecoration;
      
      results.push({
        element: `link-${index}`,
        type: 'link',
        color,
        textDecoration,
        visible: color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent'
      });
    });
    
    return results;
  }
}

// Initialize high contrast support when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.highContrastSupport = new HighContrastSupport();
  });
} else {
  window.highContrastSupport = new HighContrastSupport();
}

// Export for module usage
export default HighContrastSupport;